import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/use-toast';
import { Shield, Users, Settings } from 'lucide-react';
import { Profile } from '@/lib/supabase-types';

export function AdminPanel() {
  const [users, setUsers] = useState<Profile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { profile } = useAuth();
  const { isAdmin } = usePermissions();
  const { toast } = useToast();

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('name');

      if (error) throw error;
      
      // Transform the data to match our Profile interface
      const transformedData: Profile[] = (data || []).map(user => ({
        id: user.id,
        name: user.name,
        email: user.email,
        company: user.company,
        role: user.role,
        updated_at: user.updated_at,
        profileImage: user.profileimage, // Map profileimage to profileImage
        user_role: (user.user_role as 'user' | 'admin') || 'user',
        permissions: (user.permissions as Profile['permissions']) || {
          read: true,
          write: false,
          admin: false,
          reports: true,
        },
      }));
      
      setUsers(transformedData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserPermissions = async (userId: string, permissions: Partial<Profile['permissions']>) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ permissions })
        .eq('id', userId);

      if (error) throw error;

      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, permissions: { ...user.permissions, ...permissions } }
          : user
      ));

      toast({
        title: "Permissions updated",
        description: "User permissions have been successfully updated.",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  const makeUserAdmin = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ user_role: 'admin' })
        .eq('id', userId);

      if (error) throw error;

      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, user_role: 'admin' } : user
      ));

      toast({
        title: "Admin rights granted",
        description: "User has been granted admin privileges.",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  if (!isAdmin) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertDescription>
              You don't have permission to access the admin panel.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Admin Panel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
              <div>
                <h3 className="font-semibold">User Management</h3>
                <p className="text-sm text-muted-foreground">
                  Manage user permissions and access levels
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Users & Permissions</CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-4">
            {users.map((user) => (
              <div key={user.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold">{user.name || 'Unknown User'}</h4>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                  <div className="flex gap-2">
                    {user.user_role === 'admin' && (
                      <Badge variant="secondary">Admin</Badge>
                    )}
                    {user.id === profile?.id && (
                      <Badge variant="outline">You</Badge>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id={`read-${user.id}`}
                      checked={user.permissions?.read ?? true}
                      onCheckedChange={(checked) => 
                        updateUserPermissions(user.id, { 
                          ...user.permissions, 
                          read: checked 
                        })
                      }
                      disabled={user.id === profile?.id}
                    />
                    <Label htmlFor={`read-${user.id}`} className="text-sm">
                      Read Access
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id={`write-${user.id}`}
                      checked={user.permissions?.write ?? false}
                      onCheckedChange={(checked) => 
                        updateUserPermissions(user.id, { 
                          ...user.permissions, 
                          write: checked 
                        })
                      }
                      disabled={user.id === profile?.id || user.user_role === 'admin'}
                    />
                    <Label htmlFor={`write-${user.id}`} className="text-sm">
                      Write Access
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id={`reports-${user.id}`}
                      checked={user.permissions?.reports ?? true}
                      onCheckedChange={(checked) => 
                        updateUserPermissions(user.id, { 
                          ...user.permissions, 
                          reports: checked 
                        })
                      }
                      disabled={user.id === profile?.id}
                    />
                    <Label htmlFor={`reports-${user.id}`} className="text-sm">
                      Generate Reports
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {user.user_role !== 'admin' && user.id !== profile?.id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => makeUserAdmin(user.id)}
                      >
                        Make Admin
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
